import React, { useState, useRef, useEffect } from 'react';
import './ErrorCorrectionExercise.css';

const ECIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ErrorCorrectionExercise = ({
  sentences: initialSentences, title, level, onClose,
  updateAttributes, isStudent = false,
  myState = {}, otherState = {}, updateState = () => {},
}) => {
  const [sentences, setSentences] = useState(initialSentences);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [firstTrySuccess, setFirstTrySuccess] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [editableSentences, setEditableSentences] = useState([...initialSentences]);
  const [editableTitle, setEditableTitle] = useState(title);
  const inputRefs = useRef({});

  const firstTrySuccessRef = useRef(0);
  const wrongAttemptsRef = useRef(0);
  // Every sentence id that's ever been answered wrong in this attempt —
  // see handleAnswerSubmit for why this can't just be derived from
  // showResults (which gets reset to null after a wrong answer, to clear
  // the red highlight, so it can't distinguish "never wrong" from
  // "was wrong a moment ago").
  const wrongSentenceIdsRef = useRef(new Set());
  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);

  // Mount-restore: recover in-progress state when exercise is opened mid-session
  useEffect(() => {
    if (syncedRef.current) return;
    const src = Object.keys(myState).length > 0 ? myState : (Object.keys(otherState).length > 0 ? otherState : null);
    if (!src) return;
    const hasAnswers = src.userAnswers && Object.keys(src.userAnswers).length > 0;
    const hasResults = src.showResults && Object.values(src.showResults).some(v => v !== null && v !== undefined);
    if (!hasAnswers && !hasResults && !src.resultsVisible) return;
    syncedRef.current = true;
    if (src.userAnswers) setUserAnswers(src.userAnswers);
    if (src.showResults) setShowResults(src.showResults);
    if (typeof src.firstTrySuccess === 'number') { setFirstTrySuccess(src.firstTrySuccess); firstTrySuccessRef.current = src.firstTrySuccess; }
    if (typeof src.wrongAttempts === 'number') { setWrongAttempts(src.wrongAttempts); wrongAttemptsRef.current = src.wrongAttempts; }
    if (Array.isArray(src.wrongSentenceIds)) wrongSentenceIdsRef.current = new Set(src.wrongSentenceIds);
    if (src.resultsVisible) setResultsVisible(true);
  }, [myState, otherState]); // eslint-disable-line

  // Sync live typing from the other participant
  useEffect(() => {
    if (!otherState.userAnswers) return;
    setUserAnswers(otherState.userAnswers);
  }, [otherState.userAnswers]); // eslint-disable-line

  // Sync per-sentence check results (correct/incorrect feedback)
  useEffect(() => {
    if (!otherState.showResults) return;
    setShowResults(otherState.showResults);
  }, [otherState.showResults]); // eslint-disable-line

  // Sync final results panel
  useEffect(() => {
    if (!otherState.resultsVisible) return;
    setResultsVisible(true);
    if (typeof otherState.firstTrySuccess === 'number') { setFirstTrySuccess(otherState.firstTrySuccess); firstTrySuccessRef.current = otherState.firstTrySuccess; }
    if (typeof otherState.wrongAttempts === 'number') { setWrongAttempts(otherState.wrongAttempts); wrongAttemptsRef.current = otherState.wrongAttempts; }
  }, [otherState.resultsVisible]); // eslint-disable-line

  // Sync Try Again
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setUserAnswers({});
    setShowResults({});
    setResultsVisible(false);
    setFirstTrySuccess(0);
    setWrongAttempts(0);
    firstTrySuccessRef.current = 0;
    wrongAttemptsRef.current = 0;
    wrongSentenceIdsRef.current = new Set();
  }, [otherState.resetAt]); // eslint-disable-line

  const handleAnswerChange = (sentenceId, value) => {
    const next = { ...userAnswers, [sentenceId]: value };
    setUserAnswers(next);
    updateState({ userAnswers: next });
  };

  const handleAnswerSubmit = (sentenceId, e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const sentence = sentences.find(s => s.id === sentenceId);
    const userAnswer = userAnswers[sentenceId] || '';
    if (!sentence || !sentence.correctAnswers) return;

    const isCorrect = sentence.correctAnswers.some(answer =>
      answer.toLowerCase().trim() === userAnswer.toLowerCase().trim()
    );

    const nextShowResults = { ...showResults, [sentenceId]: isCorrect };
    setShowResults(nextShowResults);
    updateState({ showResults: nextShowResults });

    // "First try" must mean the sentence was NEVER wrong, not just "isn't
    // currently showing wrong" — showResults[sentenceId] gets reset to
    // null a second after a wrong attempt (below, to clear the red
    // highlight), so checking only !showResults[sentenceId] couldn't tell
    // a genuine first attempt from a 5th attempt that finally landed.
    // wrongSentenceIdsRef remembers every sentence that's EVER been
    // wrong, for the lifetime of this attempt (cleared on Try Again).
    if (isCorrect && !showResults[sentenceId] && !wrongSentenceIdsRef.current.has(sentenceId)) {
      const next = firstTrySuccessRef.current + 1;
      firstTrySuccessRef.current = next;
      setFirstTrySuccess(next);
    } else if (!isCorrect) {
      wrongSentenceIdsRef.current.add(sentenceId);
      updateState({ wrongSentenceIds: Array.from(wrongSentenceIdsRef.current) });
      const next = wrongAttemptsRef.current + 1;
      wrongAttemptsRef.current = next;
      setWrongAttempts(next);
    }

    if (!isCorrect) {
      setTimeout(() => {
        setShowResults(prev => {
          const next = { ...prev, [sentenceId]: null };
          updateState({ showResults: next });
          return next;
        });
      }, 1000);
    }
  };

  const handleFinish = () => {
    setResultsVisible(true);
    updateState({
      resultsVisible: true,
      firstTrySuccess: firstTrySuccessRef.current,
      wrongAttempts: wrongAttemptsRef.current,
    });
  };

  const handleTryAgain = () => {
    setUserAnswers({});
    setShowResults({});
    setResultsVisible(false);
    setFirstTrySuccess(0);
    setWrongAttempts(0);
    firstTrySuccessRef.current = 0;
    wrongAttemptsRef.current = 0;
    wrongSentenceIdsRef.current = new Set();
    updateState({
      userAnswers: {}, showResults: {}, resultsVisible: false,
      firstTrySuccess: 0, wrongAttempts: 0, wrongSentenceIds: [],
      resetAt: Date.now(),
    });
  };

  const handleAddCorrectAnswer = (sentenceId) => {
    setEditableSentences(prev => prev.map(s =>
      s.id === sentenceId
        ? { ...s, correctAnswers: [...(s.correctAnswers || []), ''] }
        : s
    ));
  };

  const handleRemoveCorrectAnswer = (sentenceId, answerIndex) => {
    setEditableSentences(prev => prev.map(s =>
      s.id === sentenceId
        ? { ...s, correctAnswers: s.correctAnswers.filter((_, i) => i !== answerIndex) }
        : s
    ));
  };

  const handleCorrectAnswerChange = (sentenceId, answerIndex, value) => {
    setEditableSentences(prev => prev.map(s =>
      s.id === sentenceId
        ? { ...s, correctAnswers: s.correctAnswers.map((answer, i) => i === answerIndex ? value : answer) }
        : s
    ));
  };

  const handleAddSentence = () => {
    const newSentence = {
      id: `sentence_${Date.now()}`,
      text: '',
      correctAnswers: [''],
      explanation: ''
    };
    setEditableSentences(prev => [...prev, newSentence]);
  };

  const handleDeleteSentence = (sentenceId) => {
    if (editableSentences.length <= 1) return;
    setEditableSentences(prev => prev.filter(s => s.id !== sentenceId));
  };

  const handleSave = () => {
    setIsEditing(false);
    setSentences(editableSentences);
    if (updateAttributes) {
      updateAttributes({
        sentences: editableSentences,
        title: editableTitle,
        level,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableSentences([...initialSentences]);
  };

  // A sentence only counts as "done" once its correction has actually been
  // verified correct — the Finish button shouldn't appear just because the
  // student pressed Enter once on a single sentence.
  const allCorrect = sentences.length > 0 && sentences.every(s => showResults[s.id] === true);

  // Results panel data — same ring/tier/headline pattern as the other
  // redesigned exercises, re-skinned with this exercise's red accent.
  let resultsData = null;
  if (resultsVisible) {
    const total = sentences.length || 1;
    // Finish only ever appears once every sentence is correct (see
    // allCorrect above), so a plain firstTrySuccess/total ratio can land
    // on a literal 0% for someone who worked through every sentence and
    // got the whole exercise right — which reads as "you failed" when
    // they didn't. Score overall efficiency instead: every sentence took
    // at least one (successful) attempt, plus however many wrong ones —
    // fewer wrong attempts relative to the sentence count means a higher
    // score, but it only approaches 0 as wrong attempts pile up, never
    // hits it outright just because first-try success happened to be 0.
    const percent = Math.round((total / (total + wrongAttempts)) * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'practice';
    let headline;
    let subline;
    if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, circumference, dashOffset, tier, headline, subline };
  }

  return (
    <div className="ec-exercise-card">
      {/* Header */}
      <div className="ec-header">
        <div className="ec-header-left">
          <div className="ec-header-icon"><ECIcon /></div>
          {isEditing ? (
            <input
              className="ec-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="ec-title">{editableTitle}</span>
          )}
          <span className="ec-level-badge">{level}</span>
        </div>
        <div className="ec-header-actions">
          {!isEditing && !isStudent && (
            <button className="ec-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          )}
          {isEditing && (
            <>
              <button className="ec-icon-btn is-save" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="ec-icon-btn is-cancel" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="ec-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="ec-body">
        {!isEditing ? (
          <div className="ec-correction-section">
            {!resultsVisible ? (
              <>
                <div className="ec-sentences-list">
                  {sentences.map((sentence, index) => (
                    <div key={sentence.id} className="ec-sentence-row">
                      <div className="ec-sentence-number">{index + 1}.</div>
                      <div className="ec-sentence-content">
                        <div className="ec-sentence-text">{sentence.text}</div>
                        <div className="ec-correction-input-wrap">
                          <input
                            ref={el => inputRefs.current[sentence.id] = el}
                            type="text"
                            placeholder="Type your corrected sentence here..."
                            value={userAnswers[sentence.id] || ''}
                            onChange={e => handleAnswerChange(sentence.id, e.target.value)}
                            onKeyDown={e => handleAnswerSubmit(sentence.id, e)}
                            className={`ec-correction-input ${showResults[sentence.id] === true ? 'correct' : showResults[sentence.id] === false ? 'incorrect' : ''}`}
                            disabled={showResults[sentence.id] === true}
                          />
                          {showResults[sentence.id] !== true && (
                            <span className="ec-correction-input-hint">Press ↵</span>
                          )}
                        </div>
                        {showResults[sentence.id] === true && (
                          <div className="ec-result-feedback correct-feedback">
                            <span className="ec-correct-icon">✔</span> Correct!
                          </div>
                        )}
                        {showResults[sentence.id] === false && (
                          <div className="ec-result-feedback incorrect-feedback">
                            <span className="ec-incorrect-icon">✗</span> Try again
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ec-controls">
                  {allCorrect && (
                    <button className="ec-finish-btn" onClick={handleFinish}>
                      Finish Exercise
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className={`ec-results tier-${resultsData.tier}`}>
                <div className="ec-results-ring-wrap">
                  <svg viewBox="0 0 100 100" className="ec-results-ring" aria-hidden>
                    <circle cx="50" cy="50" r="42" className="ec-results-ring-track" />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      className="ec-results-ring-progress"
                      style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                    />
                  </svg>
                  <div className="ec-results-ring-center">
                    <div className="ec-results-percent">{resultsData.percent}%</div>
                    <div className="ec-results-percent-label">accuracy</div>
                  </div>
                </div>
                <div className="ec-results-headline">{resultsData.headline}</div>
                <div className="ec-results-subline">{resultsData.subline}</div>
                <div className="ec-results-stats">
                  <div className="ec-results-stat is-correct">
                    <span className="ec-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className="ec-results-stat-value">{firstTrySuccess}</span>
                    <span className="ec-results-stat-label">first-try</span>
                  </div>
                  <div className="ec-results-stats-divider" aria-hidden />
                  <div className="ec-results-stat is-incorrect">
                    <span className="ec-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                    <span className="ec-results-stat-value">{wrongAttempts}</span>
                    <span className="ec-results-stat-label">wrong attempts</span>
                  </div>
                </div>
                <button className="ec-results-cta" onClick={handleTryAgain}>
                  <span>Try Again</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="ec-edit-section">
            <div className="ec-edit-sentences-list">
              {editableSentences.map((sentence, index) => (
                <div key={sentence.id} className="ec-edit-sentence-item">
                  <div className="ec-edit-sentence-header">
                    <span className="ec-edit-sentence-number">{index + 1}.</span>
                    <button
                      className="ec-delete-sentence-btn"
                      onClick={() => handleDeleteSentence(sentence.id)}
                      disabled={editableSentences.length <= 1}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <div className="ec-edit-sentence-content">
                    <div className="ec-edit-field">
                      <label>Sentence with error:</label>
                      <textarea
                        value={sentence.text}
                        onChange={e => setEditableSentences(prev => prev.map(s =>
                          s.id === sentence.id ? { ...s, text: e.target.value } : s
                        ))}
                        placeholder="Enter sentence with error..."
                        className="ec-edit-textarea"
                      />
                    </div>
                    <div className="ec-edit-field">
                      <label>Correct answers:</label>
                      {(sentence.correctAnswers || ['']).map((answer, answerIndex) => (
                        <div key={answerIndex} className="ec-correct-answer-row">
                          <input
                            type="text"
                            value={answer}
                            onChange={e => handleCorrectAnswerChange(sentence.id, answerIndex, e.target.value)}
                            placeholder="Enter correct answer..."
                            className="ec-edit-input"
                          />
                          <button
                            className="ec-remove-answer-btn"
                            onClick={() => handleRemoveCorrectAnswer(sentence.id, answerIndex)}
                            disabled={(sentence.correctAnswers || []).length <= 1}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      <button className="ec-add-answer-btn" onClick={() => handleAddCorrectAnswer(sentence.id)}>
                        + Add another correct answer
                      </button>
                    </div>
                    <div className="ec-edit-field">
                      <label>Explanation (optional):</label>
                      <textarea
                        value={sentence.explanation || ''}
                        onChange={e => setEditableSentences(prev => prev.map(s =>
                          s.id === sentence.id ? { ...s, explanation: e.target.value } : s
                        ))}
                        placeholder="Explain the correction..."
                        className="ec-edit-textarea"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="ec-add-sentence-btn" onClick={handleAddSentence}>
              + Add Sentence
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorCorrectionExercise;
